import React, { useEffect, useState } from "react";
import {
  Search,
  LayoutGrid,
  List,
  ArrowUpDown,
  LogIn,
  UserRound,
  Building2,
  Clock3,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getData, postData } from "../../helpers/url-request";
import { hasUnsavedChanges } from "../../helpers/unsaved-changes";
import { getAuthenticatedUser } from "../../helpers/auth-session";
import "./SwitchFirm.scss";
const accessed = (f) => Date.parse(f.lastAccessedOn || "") || 0;
const accessLabel = (f) =>
  f.isCurrent
    ? "Just now"
    : accessed(f)
    ? new Date(accessed(f)).toLocaleDateString()
    : "Not available";
const Avatar = ({ firm }) => (
  <span className="switch-firm__avatar" aria-hidden="true">
    {(firm.firmName || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase()}
  </span>
);
const Badge = () => <span className="switch-firm__badge">Current firm</span>;
export default function SwitchFirm() {
  const choosingAfterSignIn = Boolean(
    getAuthenticatedUser()?.requiresFirmSelection
  );
  const [firms, setFirms] = useState([]),
    [search, setSearch] = useState(""),
    [role, setRole] = useState("");
  const [sort, setSort] = useState("recent"),
    [view, setView] = useState("grid"),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(null),
    [error, setError] = useState("");
  const load = () => {
    setLoading(true);
    setError("");
    getData("/FirmMembership/mine")
      .then((response) => {
        if (!response.status)
          throw new Error(response.message || "Unable to load your firms.");
        setFirms(response.data || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => setPage(1), [search, role, sort, pageSize]);
  const selectFirm = async (firm) => {
    if (
      hasUnsavedChanges() &&
      !window.confirm("Leave this page and discard unsaved changes?")
    )
      return;
    setBusy(firm.firmId);
    setError("");
    try {
      const response = await postData("/FirmMembership/switch", {
        firmId: firm.firmId,
        expectedFirmId:
          firm.expectedFirmId ?? firms.find((item) => item.isCurrent)?.firmId,
      });
      if (!response.status)
        throw new Error(response.message || "Unable to switch firm.");
      // Full navigation disposes all firm-specific state and connections.
      window.location.assign("/piers/landing");
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };
  const visible = firms
    .filter(
      (f) =>
        `${f.firmName} ${f.roleName} ${f.branchName}`
          .toLowerCase()
          .includes(search.toLowerCase()) &&
        (!role || f.roleName === role)
    )
    .sort((a, b) => {
      if (sort === "recent")
        return (
          Number(b.isCurrent) - Number(a.isCurrent) ||
          accessed(b) - accessed(a) ||
          a.firmName.localeCompare(b.firmName)
        );
      const key = { name: "firmName", role: "roleName", branch: "branchName" }[
        sort
      ];
      return (a[key] || "").localeCompare(b[key] || "");
    });
  const pages = Math.max(1, Math.ceil(visible.length / pageSize)),
    currentPage = Math.min(page, pages);
  const shown = visible.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const recent = firms
    .filter((f) => f.isCurrent || accessed(f))
    .sort(
      (a, b) =>
        Number(b.isCurrent) - Number(a.isCurrent) || accessed(b) - accessed(a)
    )
    .slice(0, 3);
  const action = (f, compact = false) => (
    <button
      type="button"
      className={`switch-firm__action${f.isCurrent ? " is-outline" : ""}`}
      disabled={busy !== null}
      onClick={() => selectFirm(f)}
    >
      <LogIn size={20} aria-hidden="true" />
      {busy === f.firmId
        ? "Opening…"
        : choosingAfterSignIn
        ? "Continue with this firm"
        : compact
        ? f.isCurrent
          ? "Open"
          : "Switch"
        : f.isCurrent
        ? "Open current firm"
        : "Switch to this firm"}
    </button>
  );
  const filter = (label, value, setter, key, all) => (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
    >
      <option value="">{all}</option>
      {[...new Set(firms.map((f) => f[key]).filter(Boolean))]
        .sort()
        .map((name) => (
          <option key={name}>{name}</option>
        ))}
    </select>
  );
  return (
    <main className="page-content switch-firm piers-page">
      <div className="switch-firm__heading">
        <div className="piers-page-header">
          <span className="switch-firm__eyebrow">YOUR WORKSPACE</span>
          <h1 className="piers-page-title">{choosingAfterSignIn ? "Choose your firm" : "Switch firm"}</h1>
          <p>
            {choosingAfterSignIn
              ? "You belong to more than one firm. Choose which firm you want to sign in to."
              : "Choose a firm to open its workspace. Your role and access apply to the selected firm."}
          </p>
        </div>
        <svg
          className="switch-firm__illustration"
          viewBox="0 0 220 132"
          aria-hidden="true"
        >
          <ellipse cx="110" cy="78" rx="99" ry="50" fill="#edf3fb" />
          <path d="M70 126V20L126 10V126" fill="#bed5f3" />
          <path d="M126 10L145 18V126H126" fill="#6998cd" />
          <path d="M140 126V56L176 49V126" fill="#a4c4eb" />
          <path d="M176 49L192 58V126H176" fill="#6a97cb" />
          <path d="M48 126V63H79V126" fill="#d5e5fa" />
          {[0, 1, 2, 3].map((row) =>
            [0, 1, 2].map((col) => (
              <rect
                key={`${row}-${col}`}
                x={81 + col * 15}
                y={34 + row * 20}
                width="6"
                height="9"
                fill="#6796cb"
              />
            ))
          )}
          <path d="M101 126V108H115V126" fill="#6998cd" />
          <path d="M15 127H210" stroke="#86a9d2" strokeWidth="2" />
          <path d="M32 126V99M201 126V100" stroke="#6c92bf" strokeWidth="3" />
          <ellipse cx="32" cy="96" rx="10" ry="16" fill="#7da6d7" />
          <ellipse cx="201" cy="96" rx="8" ry="13" fill="#7da6d7" />
        </svg>
      </div>
      <div className="switch-firm__toolbar">
        <label className="switch-firm__search">
          <Search size={21} aria-hidden="true" />
          <input
            type="search"
            aria-label="Search your firms"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by firm name, branch or your role..."
          />
        </label>
        {filter("Filter by role", role, setRole, "roleName", "All roles")}
        <label className="switch-firm__sort">
          <ArrowUpDown size={19} aria-hidden="true" />
          <select
            aria-label="Sort firms"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="recent">Sort: Recently accessed</option>
            <option value="name">Sort: Firm name</option>
            <option value="branch">Sort: Branch</option>
            <option value="role">Sort: Your role</option>
          </select>
        </label>
        <div className="switch-firm__views" aria-label="Firm view">
          <button
            type="button"
            aria-label="Card view"
            aria-pressed={view === "grid"}
            onClick={() => setView("grid")}
          >
            <LayoutGrid size={22} />
          </button>
          <button
            type="button"
            aria-label="List view"
            aria-pressed={view === "list"}
            onClick={() => setView("list")}
          >
            <List size={23} />
          </button>
        </div>
      </div>
      {error && (
        <div role="alert" className="alert alert-danger">
          {error}{" "}
          <button type="button" onClick={load}>
            Reload firms
          </button>
        </div>
      )}
      {loading ? (
        <p role="status">Loading your firms…</p>
      ) : (
        <>
          <p className="switch-firm__count" aria-live="polite">
            {visible.length} {visible.length === 1 ? "firm" : "firms"}
          </p>
          {view === "grid" ? (
            <div className="switch-firm__grid">
              {shown.map((f) => (
                <article
                  key={f.firmId}
                  className={`switch-firm__card${
                    f.isCurrent ? " is-current" : ""
                  }`}
                >
                  <div className="switch-firm__card-heading">
                    <Avatar firm={f} />
                    <div>
                      <h2>{f.firmName}</h2>
                      <p>{f.branchName}</p>
                    </div>
                    {f.isCurrent && <Badge />}
                  </div>
                  <div className="switch-firm__details">
                    <div>
                      <UserRound aria-hidden="true" />
                      <dl>
                        <dt>Your role</dt>
                        <dd>{f.roleName}</dd>
                      </dl>
                    </div>
                    <div>
                      <Building2 aria-hidden="true" />
                      <dl>
                        <dt>Branch</dt>
                        <dd>{f.branchName}</dd>
                      </dl>
                    </div>
                  </div>
                  <div className="switch-firm__access">
                    <Clock3 aria-hidden="true" />
                    <dl>
                      <dt>Last accessed</dt>
                      <dd>{accessLabel(f)}</dd>
                    </dl>
                  </div>
                  {action(f)}
                </article>
              ))}
            </div>
          ) : (
            <div className="switch-firm__table-wrap">
              <table className="switch-firm__table">
                <thead>
                  <tr>
                    {[
                      ["Firm", "name"],
                      ["Branch", "branch"],
                      ["Your role", "role"],
                      ["Last accessed", "recent"],
                    ].map(([label, key]) => (
                      <th
                        key={key}
                        scope="col"
                        aria-sort={
                          sort === key
                            ? key === "recent"
                              ? "descending"
                              : "ascending"
                            : "none"
                        }
                      >
                        <button type="button" onClick={() => setSort(key)}>
                          {label}
                          <ArrowUpDown size={15} />
                        </button>
                      </th>
                    ))}
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f) => (
                    <tr
                      key={f.firmId}
                      className={f.isCurrent ? "is-current" : ""}
                    >
                      <td>
                        <div className="switch-firm__firm-cell">
                          <Avatar firm={f} />
                          <strong>{f.firmName}</strong>
                        </div>
                      </td>
                      <td>{f.branchName}</td>
                      <td>{f.roleName}</td>
                      <td>
                        <div className="switch-firm__last-cell">
                          {accessLabel(f)}
                          {f.isCurrent && <Badge />}
                        </div>
                      </td>
                      <td>{action(f, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!visible.length && (
            <p role="status">
              {firms.length
                ? "No firms match your search or filters."
                : "You have no active firm memberships. Contact your PIERS administrator."}
            </p>
          )}
          {(view === "list" || pages > 1) && (
            <div className="switch-firm__pagination">
              <span>
                Showing {shown.length ? (currentPage - 1) * pageSize + 1 : 0}–
                {Math.min(currentPage * pageSize, visible.length)} of{" "}
                {visible.length} firms
              </span>
              <div>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft size={20} />
                </button>
                <span aria-current="page">{currentPage}</span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={currentPage === pages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  <ChevronRight size={20} />
                </button>
                <select
                  aria-label="Firms per page"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {view === "grid" && recent.length > 0 && (
            <section
              className="switch-firm__recent"
              aria-label="Recently accessed firms"
            >
              <div className="switch-firm__recent-heading">
                <h2>
                  <Clock3 size={23} aria-hidden="true" />
                  Recently accessed firms
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setView("list");
                    setSearch("");
                    setRole("");
                    setSort("recent");
                  }}
                >
                  View all <span aria-hidden="true">&rarr;</span>
                </button>
              </div>
              {recent.map((f) => (
                <button
                  type="button"
                  className="switch-firm__recent-row"
                  key={f.firmId}
                  disabled={busy !== null}
                  onClick={() => selectFirm(f)}
                >
                  <span className="switch-firm__firm-cell">
                    <Avatar firm={f} />
                    <strong>{f.firmName}</strong>
                  </span>
                  <span>{f.branchName}</span>
                  <span>{f.roleName}</span>
                  <span>{accessLabel(f)}</span>
                </button>
              ))}
            </section>
          )}
        </>
      )}
    </main>
  );
}
